import React, { useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  FiZap, FiTarget, FiTrendingUp, FiLayers, FiAlertCircle, FiCheck, 
  FiUsers, FiX, FiClock, FiRefreshCw, FiHeart, FiArrowRight, FiStar,
  FiPlay, FiShield, FiCpu, FiPackage, FiImage, FiUpload, FiGrid,
  FiInstagram, FiYoutube, FiFacebook, FiFolder, FiBox
} from 'react-icons/fi';

// Credit-based pricing packs - Updated Feb 2026 (credits halved, 12 credits per idea)
const PRICING_PACKS = [
  { 
    id: 'starter', 
    name: 'Starter', 
    credits: 100,
    approxIdeas: 8, 
    price: 5499, 
    priceDisplay: '₹5,499', 
    features: ['100 credits (~8 ideas)', 'Captions + images', 'Brand memory', '3 refinements per content'] 
  },
  { 
    id: 'growth', 
    name: 'Growth', 
    credits: 200,
    approxIdeas: 16, 
    price: 9999, 
    priceDisplay: '₹9,999', 
    features: ['200 credits (~16 ideas)', 'Captions + images', 'Brand memory', '3 refinements per content', 'AI adapts to your feedback'],
    isPopular: true 
  },
  { 
    id: 'scale', 
    name: 'Scale', 
    credits: 400,
    approxIdeas: 33, 
    price: 17999, 
    priceDisplay: '₹17,999', 
    features: ['400 credits (~33 ideas)', 'Captions + images', 'Brand memory', '3 refinements per content', 'AI adapts to your feedback', 'Best value'] 
  },
];

const SUBSCRIPTION = {
  id: 'monthly',
  name: 'Monthly Plan',
  credits: 200,
  approxIdeas: 16,
  price: 9999,
  priceDisplay: '₹9,999',
  savings: 'Most popular',
  features: ['200 credits/month (~16 ideas)', 'Priority generation', 'Cancel anytime']
};

const COMPARISON_DATA = [
  { feature: 'Brand context', firstcut: 'Saved & reused', others: 'Re-enter every session' },
  { feature: 'Content freshness', firstcut: 'Helps avoid repetition', others: 'Can get repetitive' },
  { feature: 'AI learning', firstcut: 'Adapts to your feedback', others: 'Static prompts' },
  { feature: 'Image generation', firstcut: 'Included', others: 'Extra cost' },
  { feature: 'Format optimization', firstcut: 'Platform-native', others: 'Same template' },
  { feature: 'Time to usable content', firstcut: 'Typically under 5 min', others: '30+ min editing' },
];

function LandingPage() {
  const { login } = useAuth();
  
  const sessionExpired = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const expired = params.get('session_expired') === 'true';
    if (expired) {
      window.history.replaceState({}, '', '/');
    }
    return expired;
  }, []);

  const sessionTimeout = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const timeout = params.get('session_timeout') === 'true';
    if (timeout) {
      window.history.replaceState({}, '', '/');
    }
    return timeout;
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
      {/* Background gradient orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 -left-40 w-80 h-80 bg-accent/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />
      </div>
      
      {/* Session Expired Banner */}
      {sessionExpired && (
        <div className="relative z-50 bg-accent text-accent-foreground px-6 py-3 text-center flex items-center justify-center">
          <FiAlertCircle className="mr-2" />
          <span>Your session has expired. Please sign in again to continue.</span>
        </div>
      )}

      {/* Session Timeout Banner */}
      {sessionTimeout && !sessionExpired && (
        <div className="relative z-50 bg-amber-500 text-white px-6 py-3 text-center flex items-center justify-center">
          <FiClock className="mr-2" />
          <span>You were logged out due to inactivity. Sign in to continue where you left off.</span>
        </div>
      )}
      
      {/* Header */}
      <header className="relative z-10 container mx-auto px-6 py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary/50 rounded-xl flex items-center justify-center shadow-lg shadow-primary/25">
              <FiZap className="text-white" size={22} />
            </div>
            <span className="text-2xl font-heading font-bold text-foreground">firstcut<span className="text-primary">.me</span></span>
          </div>
          <div className="flex items-center gap-6">
            <a href="#features" className="text-muted-foreground hover:text-foreground transition-colors hidden md:block text-sm font-medium">Features</a>
            <a href="#pricing" className="text-muted-foreground hover:text-foreground transition-colors hidden md:block text-sm font-medium">Pricing</a>
            <a href="#comparison" className="text-muted-foreground hover:text-foreground transition-colors hidden md:block text-sm font-medium">Compare</a>
            <button
              onClick={login}
              className="px-5 py-2.5 bg-secondary hover:bg-secondary/80 text-foreground rounded-lg font-medium transition-all text-sm border border-border"
              data-testid="login-btn"
            >
              Sign In
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main className="relative z-10 container mx-auto px-6 pt-16 md:pt-24 pb-24">
        <div className="max-w-5xl mx-auto">
          {/* Pain Point Hook */}
          <div className="flex justify-center mb-8 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 border border-accent/20 rounded-full text-accent text-sm font-medium">
              <FiAlertCircle size={16} />
              <span>Spending hours on every social media post?</span>
            </div>
          </div>
          
          <div className="text-center">
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-foreground mb-6 leading-[1.1] tracking-tight animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              Stop Spending Hours
              <span className="block mt-2 bg-gradient-to-r from-primary via-primary to-accent bg-clip-text text-transparent animate-gradient-x">
                On Every Post
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              FirstCut learns your brand's voice, remembers what's worked, and helps you create on-brand content in minutes. Have product photos? We'll feature them. Don't? We've still got you.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              <button
                onClick={login}
                className="group px-8 py-4 bg-primary text-primary-foreground rounded-xl font-semibold text-lg hover:bg-primary/90 transition-all shadow-lg shadow-primary/25 flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-[0.98]"
                data-testid="get-started-btn"
              >
                Try 2 Posts Free
                <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
              </button>
              <a 
                href="#comparison"
                className="px-8 py-4 bg-secondary text-foreground rounded-xl font-semibold text-lg hover:bg-secondary/80 transition-all border border-border flex items-center justify-center gap-2"
              >
                <FiPlay size={18} />
                See How It Works
              </a>
            </div>
            
            <p className="text-muted-foreground/70 text-sm animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
              No credit card required • Setup in 2 minutes
            </p>
          </div>

          {/* Stats Bar */}
          <div className="mt-16 grid grid-cols-3 gap-4 max-w-2xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
            {[
              { value: 'Fast', label: 'Built to save you time' },
              { value: 'Fresh', label: 'Designed to stay fresh' },
              { value: '< 5 min', label: 'Typically to usable content' },
            ].map((stat, idx) => (
              <div key={idx} className="text-center p-4 bg-card/50 rounded-xl border border-border/50 backdrop-blur-sm">
                <div className="text-2xl md:text-3xl font-heading font-bold text-foreground">{stat.value}</div>
                <div className="text-xs md:text-sm text-muted-foreground mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Pain Points Section */}
        <div className="mt-32 max-w-4xl mx-auto">
          <p className="text-center text-muted-foreground/70 uppercase tracking-widest text-xs font-medium mb-8">Sound familiar?</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 stagger-children">
            {[
              { icon: FiRefreshCw, title: "Re-explaining brand context", desc: "Every. Single. Session.", color: "text-red-400" },
              { icon: FiClock, title: "30 mins editing AI slop", desc: "Into something usable", color: "text-amber-400" },
              { icon: FiLayers, title: "Same ideas, different day", desc: '"Another cozy morning ritual?"', color: "text-orange-400" },
            ].map((point, idx) => (
              <div key={idx} className="group p-6 bg-card rounded-xl border border-border/50 hover:border-accent/30 transition-all hover-lift">
                <point.icon className={`${point.color} mb-4 group-hover:scale-110 transition-transform`} size={28} />
                <h3 className="text-foreground font-semibold mb-2">{point.title}</h3>
                <p className="text-muted-foreground text-sm">{point.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Features Section */}
        <div id="features" className="mt-32 max-w-6xl mx-auto scroll-mt-24">
          <div className="text-center mb-16">
            <p className="text-primary text-sm font-medium uppercase tracking-widest mb-4">The Solution</p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground mb-4">
              How FirstCut Helps
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Three things that make content creation faster
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 stagger-children">
            {/* Feature 1 */}
            <div className="group p-8 bg-card rounded-2xl border border-border/50 hover:border-primary/30 transition-all hover-lift">
              <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                <FiTarget className="text-primary" size={28} />
              </div>
              <h3 className="font-heading text-xl font-bold text-foreground mb-3">Brand Memory</h3>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Set up your brand once. Tone, audience, constraints — saved and reused. Less re-explaining, more creating.
              </p>
              <div className="bg-secondary/50 rounded-lg p-4 text-sm space-y-3 border border-border/30">
                <div>
                  <span className="text-muted-foreground/70 text-xs">Other tools:</span>
                  <p className="text-red-400/80 line-through mt-1">"We're a premium coffee brand..."</p>
                </div>
                <div>
                  <span className="text-muted-foreground/70 text-xs">FirstCut:</span>
                  <p className="text-emerald-400 mt-1 flex items-center gap-2">
                    <FiCheck size={14} />
                    Just click "Generate"
                  </p>
                </div>
              </div>
            </div>
            
            {/* Feature 2 */}
            <div className="group p-8 bg-card rounded-2xl border border-border/50 hover:border-accent/30 transition-all hover-lift">
              <div className="w-14 h-14 bg-accent/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-accent/20 transition-colors">
                <FiHeart className="text-accent" size={28} />
              </div>
              <h3 className="font-heading text-xl font-bold text-foreground mb-3">AI That Adapts</h3>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Approve or reject content. The AI notices patterns and tends to improve the more you use it.
              </p>
              <div className="bg-secondary/50 rounded-lg p-4 text-sm border border-border/30">
                <div className="flex items-center gap-2 mb-3">
                  <FiTrendingUp className="text-emerald-400" size={16} />
                  <span className="text-muted-foreground">Gets better with use</span>
                </div>
                <p className="text-muted-foreground/60 text-xs italic">The more feedback you give, the more it learns your preferences</p>
              </div>
            </div>
            
            {/* Feature 3 */}
            <div className="group p-8 bg-card rounded-2xl border border-border/50 hover:border-primary/30 transition-all hover-lift">
              <div className="w-14 h-14 bg-cyan-500/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-cyan-500/20 transition-colors">
                <FiShield className="text-cyan-400" size={28} />
              </div>
              <h3 className="font-heading text-xl font-bold text-foreground mb-3">Helps Keep Ideas Fresh</h3>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Smart detection helps flag similar concepts you've used before. Fewer "didn't we post something like this?" moments.
              </p>
              <div className="bg-secondary/50 rounded-lg p-4 text-sm border border-border/30">
                <span className="text-muted-foreground/70 text-xs">Designed to catch:</span>
                <div className="mt-2 space-y-2">
                  <div className="text-amber-400/80 flex items-center gap-2">
                    <FiAlertCircle size={14} />
                    <span>Similar hooks</span>
                  </div>
                  <div className="text-amber-400/80 flex items-center gap-2">
                    <FiAlertCircle size={14} />
                    <span>Repeated angles</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* NEW: Product-First Flow Section */}
        <div className="mt-32 max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-accent text-sm font-medium uppercase tracking-widest mb-4">How It Works</p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground mb-4">
              From Brand Setup to <span className="text-primary">Ready-to-Post</span> Content
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Set up your brand once. We'll remember your voice, style, and what's worked — then help you create on-brand content when you need it.
            </p>
          </div>

          {/* 4-Step Flow Visual */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-12">
            {[
              { 
                step: '1', 
                icon: FiUsers, 
                title: 'Add Your Brand', 
                desc: 'Voice, style, audience',
                detail: 'We remember — less re-explaining',
                color: 'from-purple-500 to-purple-600'
              },
              { 
                step: '2', 
                icon: FiTarget, 
                title: 'Choose Direction', 
                desc: 'AI suggests creative angles',
                detail: 'Based on your brand and goals',
                color: 'from-blue-500 to-blue-600'
              },
              { 
                step: '3', 
                icon: FiGrid, 
                title: 'Pick Platforms', 
                desc: 'Instagram, Facebook, YouTube',
                detail: 'Each format optimized for its platform',
                color: 'from-cyan-500 to-cyan-600'
              },
              { 
                step: '4', 
                icon: FiZap, 
                title: 'Generate', 
                desc: 'Captions + visuals, ready to review',
                detail: 'On-brand content, typically under 5 minutes',
                color: 'from-emerald-500 to-emerald-600'
              },
            ].map((item, idx) => (
              <div key={idx} className="relative">
                <div className="p-6 bg-card rounded-2xl border border-border/50 h-full hover:border-primary/30 transition-all hover-lift">
                  <div className={`w-12 h-12 bg-gradient-to-br ${item.color} rounded-xl flex items-center justify-center mb-4 shadow-lg`}>
                    <item.icon className="text-white" size={24} />
                  </div>
                  <div className="text-xs text-muted-foreground mb-1">Step {item.step}</div>
                  <h3 className="font-heading text-lg font-bold text-foreground mb-2">{item.title}</h3>
                  <p className="text-muted-foreground text-sm mb-3">{item.desc}</p>
                  <div className="text-xs text-primary/80 bg-primary/5 px-3 py-2 rounded-lg border border-primary/10">
                    {item.detail}
                  </div>
                </div>
                {idx < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-2 transform -translate-y-1/2 z-10">
                    <FiArrowRight className="text-muted-foreground/30" size={20} />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Product Photos - Optional Enhancement */}
          <div className="max-w-4xl mx-auto mb-8">
            <div className="p-6 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-2xl border border-purple-500/20">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FiPackage className="text-purple-400" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-lg font-bold text-foreground mb-2">
                    Have product photos? Even better.
                  </h3>
                  <p className="text-muted-foreground text-sm mb-3">
                    Upload your product images and we'll generate content featuring YOUR actual products — skipping expensive photoshoots. But it's completely optional. No product photos? We'll still create stunning on-brand content for you.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <div className="flex items-center gap-2 text-sm">
                      <FiCheck className="text-emerald-400" size={16} />
                      <span className="text-muted-foreground">AI identifies your product</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <FiCheck className="text-emerald-400" size={16} />
                      <span className="text-muted-foreground">Suggests relevant contexts</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <FiCheck className="text-emerald-400" size={16} />
                      <span className="text-muted-foreground">Features it in generated visuals</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Before/After Visual - Focus on Brand Memory */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="p-6 bg-red-500/5 rounded-2xl border border-red-500/20">
              <div className="flex items-center gap-2 mb-4">
                <FiX className="text-red-400" size={20} />
                <span className="text-red-400 font-medium">Other AI Tools</span>
              </div>
              <div className="text-muted-foreground text-sm space-y-2">
                <p>Every. Single. Time:</p>
                <p className="text-red-400/70 italic">"We're a premium furniture brand targeting urban millennials with a warm, sophisticated tone..."</p>
                <p className="text-xs text-red-400/50 mt-2">*Copy-paste the same brief for the 100th time*</p>
              </div>
            </div>
            <div className="p-6 bg-emerald-500/5 rounded-2xl border border-emerald-500/20">
              <div className="flex items-center gap-2 mb-4">
                <FiCheck className="text-emerald-400" size={20} />
                <span className="text-emerald-400 font-medium">FirstCut</span>
              </div>
              <div className="text-muted-foreground text-sm space-y-2">
                <p>Set up once. Then just:</p>
                <p className="text-emerald-400/70 italic">"Generate 5 Instagram posts for the new collection"</p>
                <p className="text-xs text-emerald-400/50 mt-2">*AI already knows your brand, audience, and tone*</p>
              </div>
            </div>
          </div>
        </div>

        {/* NEW: Platform Support Section */}
        <div className="mt-32 max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-primary text-sm font-medium uppercase tracking-widest mb-4">Multi-Platform Native</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              One Click. Every Platform.
            </h2>
            <p className="text-muted-foreground text-lg">
              Each piece optimized for its platform — not the same content copy-pasted everywhere.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { 
                icon: FiInstagram, 
                name: 'Instagram', 
                color: 'from-pink-500 to-purple-500',
                available: ['Posts (1:1, 4:5)', 'Carousels'],
                coming: ['Reels (9:16)', 'Stories']
              },
              { 
                icon: FiFacebook, 
                name: 'Facebook', 
                color: 'from-blue-500 to-blue-600',
                available: ['Posts'],
                coming: ['Videos', 'Stories']
              },
              { 
                icon: FiYoutube, 
                name: 'YouTube', 
                color: 'from-red-500 to-red-600',
                available: ['Community posts'],
                coming: ['Shorts (9:16)', 'Long-form videos']
              },
            ].map((platform, idx) => (
              <div key={idx} className="p-6 bg-card rounded-2xl border border-border/50 hover:border-primary/30 transition-all hover-lift text-center">
                <div className={`w-16 h-16 bg-gradient-to-br ${platform.color} rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                  <platform.icon className="text-white" size={32} />
                </div>
                <h3 className="font-heading text-xl font-bold text-foreground mb-3">{platform.name}</h3>
                <div className="space-y-2 mb-4">
                  {platform.available.map((format, fIdx) => (
                    <div key={fIdx} className="text-sm text-muted-foreground flex items-center justify-center gap-2">
                      <FiCheck className="text-emerald-400" size={14} />
                      {format}
                    </div>
                  ))}
                </div>
                {platform.coming.length > 0 && (
                  <div className="pt-3 border-t border-border/30 space-y-2">
                    {platform.coming.map((format, fIdx) => (
                      <div key={fIdx} className="text-sm text-muted-foreground/60 flex items-center justify-center gap-2">
                        <FiClock className="text-amber-400/60" size={14} />
                        <span>{format}</span>
                        <span className="text-[10px] bg-amber-500/10 text-amber-400 px-1.5 py-0.5 rounded">Soon</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* NEW: Brand Assets Section */}
        <div className="mt-32 max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-accent text-sm font-medium uppercase tracking-widest mb-4">Brand Assets Library</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
                Upload Once. Use Forever.
              </h2>
              <p className="text-muted-foreground text-lg mb-6">
                Your logos, products, style references, and brand elements — automatically applied to every piece of content.
              </p>
              <div className="space-y-4">
                {[
                  { icon: FiBox, title: 'Product Photos', desc: 'Feature YOUR actual products in generated images' },
                  { icon: FiImage, title: 'Logos & Brand Elements', desc: 'Consistent branding across all content' },
                  { icon: FiFolder, title: 'Style References', desc: 'AI follows your visual direction' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="text-primary" size={20} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground">{item.title}</h4>
                      <p className="text-muted-foreground text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Visual Mockup */}
            <div className="relative">
              <div className="bg-card rounded-2xl border border-border/50 p-6 shadow-xl">
                <div className="flex items-center gap-2 mb-4">
                  <FiPackage className="text-primary" size={20} />
                  <span className="font-semibold text-foreground">Brand Assets</span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {['Logo', 'Product 1', 'Product 2', 'Style Ref', 'Element', 'Product 3'].map((item, idx) => (
                    <div key={idx} className="aspect-square bg-secondary rounded-lg flex items-center justify-center border border-border/30 hover:border-primary/30 transition-colors">
                      <span className="text-xs text-muted-foreground">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                  <div className="flex items-center gap-2">
                    <FiCheck className="text-emerald-400" size={16} />
                    <span className="text-emerald-400 text-sm font-medium">Auto-applied to all content</span>
                  </div>
                </div>
              </div>
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary/10 rounded-full blur-2xl" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-accent/10 rounded-full blur-2xl" />
            </div>
          </div>
        </div>

        {/* Comparison Section */}
        <div id="comparison" className="mt-32 max-w-4xl mx-auto scroll-mt-24">
          <div className="text-center mb-12">
            <p className="text-primary text-sm font-medium uppercase tracking-widest mb-4">Why FirstCut</p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground mb-4">
              vs. "Unlimited" AI Tools
            </h2>
            <p className="text-muted-foreground text-lg">
              "Unlimited" sounds great until you're spending 30 mins editing each post
            </p>
          </div>
          
          <div className="bg-card rounded-2xl border border-border/50 overflow-hidden shadow-xl">
            <div className="grid grid-cols-3 bg-secondary/50 border-b border-border/50">
              <div className="p-4 text-muted-foreground font-medium text-sm">Feature</div>
              <div className="p-4 text-center">
                <span className="text-primary font-bold">FirstCut</span>
              </div>
              <div className="p-4 text-center text-muted-foreground text-sm">Other AI Tools</div>
            </div>
            
            {COMPARISON_DATA.map((row, idx) => (
              <div key={idx} className={`grid grid-cols-3 ${idx !== COMPARISON_DATA.length - 1 ? 'border-b border-border/30' : ''} hover:bg-secondary/20 transition-colors`}>
                <div className="p-4 text-foreground/80 text-sm">{row.feature}</div>
                <div className="p-4 text-center text-emerald-400 flex items-center justify-center gap-2 text-sm font-medium">
                  <FiCheck size={16} />
                  <span>{row.firstcut}</span>
                </div>
                <div className="p-4 text-center text-muted-foreground/60 text-sm">{row.others}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Value Justification */}
        <div className="mt-24 max-w-3xl mx-auto">
          <div className="bg-gradient-to-br from-primary/5 via-card to-accent/5 rounded-2xl p-8 md:p-10 border border-border/50 shadow-xl">
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-8 text-center">
              What's Included in Every Post
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-foreground font-semibold mb-4 flex items-center gap-2">
                  <FiCheck className="text-emerald-400" />
                  Complete, ready-to-use content:
                </h4>
                <ul className="space-y-3 text-muted-foreground text-sm">
                  {[
                    'AI-generated images (multiple variations)',
                    'Platform-optimized captions',
                    'Hooks, CTAs, hashtags',
                    'Freshness detection',
                    'Brand context applied',
                    'Adapts based on your feedback'
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h4 className="text-foreground font-semibold mb-4">Compare the alternatives:</h4>
                <div className="space-y-3 text-sm">
                  {[
                    { name: 'Freelancer per post', price: '₹500-2,000', color: 'text-red-400' },
                    { name: 'Content studio retainer', price: '₹25,000+/mo', color: 'text-red-400' },
                    { name: '"Unlimited" tool + editing', price: '₹1,350 + hours', color: 'text-amber-400' },
                  ].map((item, idx) => (
                    <div key={idx} className="flex justify-between text-muted-foreground">
                      <span>{item.name}</span>
                      <span className={item.color}>{item.price}</span>
                    </div>
                  ))}
                  <div className="flex justify-between border-t border-border/50 pt-3 mt-3">
                    <span className="text-foreground font-semibold">FirstCut</span>
                    <span className="text-emerald-400 font-bold">From ₹270/post</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing Section */}
        <div id="pricing" className="mt-32 max-w-5xl mx-auto scroll-mt-24">
          <div className="text-center mb-12">
            <p className="text-primary text-sm font-medium uppercase tracking-widest mb-4">Pricing</p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground mb-4">
              Buy Credits. Create Content.
            </h2>
            <p className="text-muted-foreground text-lg">No subscriptions required. Credits never expire.</p>
          </div>
          
          {/* Credit Packs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {PRICING_PACKS.map((pack) => (
              <div
                key={pack.id}
                className={`relative p-8 bg-card rounded-2xl border transition-all hover-lift ${
                  pack.isPopular 
                    ? 'border-primary shadow-xl shadow-primary/10' 
                    : 'border-border/50'
                }`}
              >
                {pack.isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full">
                    Best Value
                  </div>
                )}
                <h3 className="font-heading text-xl font-bold text-foreground mb-2">{pack.name}</h3>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-4xl font-heading font-bold text-foreground">{pack.priceDisplay}</span>
                </div>
                <div className="text-sm text-muted-foreground mb-2">
                  {pack.credits} credits • ~{pack.approxIdeas} ideas
                </div>
                <ul className="space-y-3 mb-8 mt-6">
                  {pack.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-muted-foreground text-sm">
                      <FiCheck className="text-emerald-400 mr-3 flex-shrink-0" size={16} />
                      {feature}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={login}
                  className={`w-full py-3 rounded-lg font-medium transition-all text-sm ${
                    pack.isPopular 
                      ? 'bg-primary text-primary-foreground hover:bg-primary/90' 
                      : 'bg-secondary text-foreground hover:bg-secondary/80 border border-border/50'
                  }`}
                >
                  Get Started
                </button>
              </div>
            ))}
          </div>

          {/* Subscription Option */}
          <div className="max-w-2xl mx-auto">
            <div className="p-6 bg-gradient-to-r from-primary/5 to-accent/5 rounded-2xl border border-primary/20">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <FiZap className="text-primary" size={20} />
                    <span className="font-heading font-bold text-foreground text-lg">Or Subscribe & Save</span>
                  </div>
                  <p className="text-muted-foreground text-sm mb-2">
                    <span className="text-foreground font-semibold">{SUBSCRIPTION.priceDisplay}/month</span> → {SUBSCRIPTION.credits} credits (~{SUBSCRIPTION.approxIdeas} ideas) + priority generation
                  </p>
                  <p className="text-xs text-emerald-400">{SUBSCRIPTION.savings} • Cancel anytime</p>
                </div>
                <button
                  onClick={login}
                  className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-all text-sm whitespace-nowrap"
                >
                  Subscribe Monthly
                </button>
              </div>
            </div>
          </div>
          
          <p className="text-center text-muted-foreground/60 text-sm mt-8">
            Need more? Top up anytime. 
            <br className="hidden md:block" />
            <span className="text-xs">Try FirstCut free with 40 credits (~2 ideas) — no credit card required.</span>
          </p>
        </div>

        {/* Final CTA */}
        <div className="mt-32 max-w-2xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
            Ready to Create Content Faster?
          </h2>
          <p className="text-muted-foreground text-lg mb-8">
            Stop re-entering your brand details. Start creating on-brand content in minutes.
          </p>
          <button
            onClick={login}
            className="group px-8 py-4 bg-primary text-primary-foreground rounded-xl font-semibold text-lg hover:bg-primary/90 transition-all shadow-lg shadow-primary/25 inline-flex items-center gap-2 hover:scale-[1.02] active:scale-[0.98]"
          >
            Try 2 Posts Free
            <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-border/50 mt-24">
        <div className="container mx-auto px-6 py-12">
          <div className="flex flex-col gap-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-primary to-primary/50 rounded-lg flex items-center justify-center">
                  <FiZap className="text-white" size={16} />
                </div>
                <span className="text-lg font-heading font-bold text-foreground">firstcut<span className="text-primary">.me</span></span>
              </div>
              
              <div className="flex flex-wrap justify-center gap-6 text-sm">
                <a href="/help" className="text-muted-foreground hover:text-foreground transition-colors">Help Center</a>
                <a href="/contact-us" className="text-muted-foreground hover:text-foreground transition-colors">Contact Us</a>
                <a href="#pricing" className="text-muted-foreground hover:text-foreground transition-colors">Pricing</a>
              </div>
            </div>
            
            {/* Policy Links - Required for Razorpay */}
            <div className="flex flex-wrap justify-center gap-6 text-sm border-t border-border/30 pt-6">
              <a href="/terms" className="text-muted-foreground hover:text-foreground transition-colors">Terms of Service</a>
              <a href="/privacy" className="text-muted-foreground hover:text-foreground transition-colors">Privacy Policy</a>
              <a href="/refund" className="text-muted-foreground hover:text-foreground transition-colors">Cancellation & Refund Policy</a>
            </div>

            {/* Regulatory Disclaimer */}
            <div className="text-center border-t border-border/30 pt-6">
              <p className="text-muted-foreground/50 text-xs max-w-2xl mx-auto leading-relaxed">
                AI-generated content may be subject to disclosure requirements when published on social media. 
                Users are responsible for compliance with applicable platform and regulatory guidelines. 
                We'll notify you of significant regulatory changes.
              </p>
            </div>
            
            <p className="text-muted-foreground/60 text-sm text-center">
              © 2026 firstcut.me — Built for D2C brands who value their time and quality.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
